// Single source of truth for the dashboard's subscription-state machine.
// The plan field on UsageSummary carries enough to differentiate every
// retention-relevant state, but the rules sprawl across pages otherwise
// (billing, home, usage, playground each had its own conditional). This
// module collapses them into one pure function so banner/card/panel logic
// stays in lockstep.
//
// State semantics:
//   • 'none'              — healthy. Don't render any retention surface.
//   • 'scheduled-cancel'  — sub still active, cancellation scheduled. Service
//                           keeps working until cancelAt; show countdown +
//                           soft Resume CTA.
//   • 'canceled'          — sub status is 'canceled'. Service is gated (the
//                           backend returns 402). Show prominent Reactivate
//                           CTA on every surface.
//   • 'past-due'          — payment retry window OR anomaly-breaker freeze.
//                           Different remedy: customer must update their
//                           payment method via the Stripe Customer Portal,
//                           not Resume the cancel schedule.
//
// The 'frozen' branch is collapsed into 'past-due' for UI purposes since the
// remedy is the same (update payment / contact support to clear the freeze).
// The backend keeps them as separate verdicts (see plan-enforcer.ts) — that
// distinction matters for ops tooling but not for the customer.

import type { UsageSummary } from '@cortex/shared';

export type SubscriptionState =
  | 'none'
  | 'scheduled-cancel'
  | 'canceled'
  | 'past-due';

export function deriveSubscriptionState(plan: UsageSummary['plan']): SubscriptionState {
  // Frozen takes precedence — even on a sub that's marked active, frozen
  // means the platform has cut off chat at the gate. The customer needs
  // payment-method intervention regardless of cancel/active status.
  if (plan.frozen) return 'past-due';

  if (plan.status === 'canceled') return 'canceled';
  if (plan.status === 'past_due') return 'past-due';

  // Scheduled cancellation: Stripe sets cancel_at while keeping status
  // 'active' until the period ends. Service still works, but we want to
  // surface the countdown so the customer can change their mind.
  if (plan.cancelAt !== null && plan.status === 'active') return 'scheduled-cancel';

  return 'none';
}
